import { useEffect, useReducer, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { apiGet, apiPost } from "../api/http";
import { useAuditProgress } from "../hooks/useAuditProgress";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useActiveSite } from "../context/ActiveSiteContext";
import SeoScoreCard from "./SeoScoreCard";
import PlanUpgradeCard, { PlanUpgradeModal } from "./PlanUpgradeCard";
import { parseApiError } from "../utils/apiErrors";
import { isValidWebsiteUrl, normalizeSiteUrl } from "../utils/urlValidation";
import AuditInput from "./AuditInput";
import AuditProgress from "./AuditProgress";
import AuditResults from "./AuditResults";
import ShareCard from "./ShareCard";
import EmbedCard from "./EmbedCard";
import AuditFeedbackWidget from "./AuditFeedbackWidget";
import WorkflowSteps from "./WorkflowSteps";
import { getAppStage } from "../utils/appStage";

const PHASES = {
  INPUT: "input",
  PROGRESS: "progress",
  RESULTS: "results",
};

const demoSeoReport = {
  seo_score: 82,
  title: "Example High-Ranking Website | Best SEO Tools",
  description: "Discover how to optimize your website with our comprehensive SEO tools. Improve your rankings and drive more traffic today.",
  h1: "Boost Your Search Visibility",
  canonical_url: "https://example.com/",
  meta_robots: "index, follow",
  images: { total: 12, missingAlt: 1 },
  h2: ["Core Features", "Pricing Plans", "Success Stories", "Get Started"],
  scoreSections: {
    title: { score: 95 },
    description: { score: 90 },
    h1: { score: 100 },
    canonical: { score: 100 },
    metaRobots: { score: 100 },
    images: { score: 85 },
    headingStructure: { score: 80 },
    keywords: { score: 75 },
    https: { score: 100 },
    structuredData: { score: 60 },
    pageSpeed: { score: 45 },
  },
  issues: [
    { category: "pageSpeed", issue: "Large layout shifts detected (CLS: 0.15). Optimize image dimensions.", score: 45 },
    { category: "structuredData", issue: "Missing Schema.org markup for Organization.", score: 60 },
    { category: "keywords", issue: "Primary keyword 'SEO tools' density is a bit high (4.2%).", score: 75 },
    { category: "images", issue: "1 image is missing an 'alt' attribute.", score: 85 },
  ],
  topKeywords: [
    { word: "seo", count: 18, density: 4.5, inTitle: true, inDescription: true, inH1: true },
    { word: "tools", count: 12, density: 3.0, inTitle: true, inDescription: true, inH1: true },
    { word: "optimize", count: 8, density: 2.0, inTitle: false, inDescription: true, inH1: false },
    { word: "traffic", count: 5, density: 1.2, inTitle: false, inDescription: true, inH1: false },
  ],
  url: "https://example.com",
  created_at: new Date().toISOString(),
};

const initialState = {
  phase: PHASES.INPUT,
  url: "",
  report: null,
  loading: false,
  error: null,
};

function auditReducer(state, action) {
  switch (action.type) {
    case "SET_URL":
      return { ...state, url: action.payload };
    case "START_AUDIT":
      return { ...state, phase: PHASES.PROGRESS, loading: true, report: null, error: null };
    case "AUDIT_SUCCESS":
      return { ...state, phase: PHASES.RESULTS, loading: false, report: action.payload };
    case "AUDIT_ERROR":
      return { ...state, loading: false, error: action.payload };
    case "RESET":
      return initialState;
    default:
      return state;
  }
}

function FeedbackModal({ auditId, url, onClose, mandatory = false }) {
  return (
    <div style={{
      position: "fixed",
      inset: 0,
      background: "rgba(15, 23, 42, 0.6)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1200,
      padding: 20,
    }}>
      <div style={{
        width: "min(560px, 100%)",
        background: "var(--bg, #ffffff)",
        borderRadius: 16,
        boxShadow: "0 24px 80px rgba(15,23,42,0.18)",
        overflow: "hidden",
      }}>
        <div style={{
          display: "flex",
          justifyContent: mandatory ? "center" : "space-between",
          alignItems: "center",
          padding: "14px 20px",
          borderBottom: "1px solid var(--border, #e5e7eb)",
        }}>
          <span style={{ fontWeight: 500, fontSize: "1rem" }}>Was this audit helpful?</span>
          {!mandatory && (
            <button
              type="button"
              onClick={onClose}
              className="btn btn-ghost btn-sm"
              aria-label="Close feedback"
            >
              ×
            </button>
          )}
        </div>
        <div style={{ padding: "20px" }}>
          <AuditFeedbackWidget auditId={auditId} url={url} onSubmitted={onClose} />
        </div>
      </div>
    </div>
  );
}

export default function SeoAuditExperience({ mode = "guest" }) {
  const { isAuthenticated, user } = useAuth();
  const { showSuccess, showError } = useToast();
  const { activeSite } = useActiveSite();
  const [state, dispatch] = useReducer(auditReducer, initialState);
  const [planGate, setPlanGate] = useState(null);
  const [upgradeCardDismissed, setUpgradeCardDismissed] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [anonymousFeedbackSubmitted, setAnonymousFeedbackSubmitted] = useState(false);
  const { progress, subscribe, reset } = useAuditProgress();
  const isPremium = user?.plan_name === "solo" || user?.plan_name === "agency" || user?.plan_name === "premium";
  const isAppMode = mode === "app";

  const displayReport =
    state.report || (!isAppMode && state.phase === PHASES.INPUT ? demoSeoReport : null);

  useEffect(() => {
    if (isAppMode && activeSite && state.phase === PHASES.INPUT) {
      dispatch({ type: "SET_URL", payload: activeSite.url });
    }
  }, [activeSite, isAppMode, state.phase]);

  useEffect(() => {
    if (progress?.done && progress.result) {
      dispatch({ type: "AUDIT_SUCCESS", payload: progress.result });
      showSuccess("SEO audit completed successfully.");
      if (!isAuthenticated) {
        const today = new Date().toDateString();
        const dateKey = "anon_audits_date";
        const countKey = "anon_audits_count";
        const storedDate = localStorage.getItem(dateKey);
        let count = 0;
        if (storedDate === today) {
          count = parseInt(localStorage.getItem(countKey) || "0", 10);
        }
        localStorage.setItem(dateKey, today);
        localStorage.setItem(countKey, String(count + 1));
      }
    }
  }, [progress, isAuthenticated, showSuccess]);

  useEffect(() => {
    if (progress?.error) {
      dispatch({ type: "AUDIT_ERROR", payload: progress.error });
    }
  }, [progress]);

  useEffect(() => {
    if (state.phase === PHASES.PROGRESS) {
      setUpgradeCardDismissed(false);
      setFeedbackModalOpen(false);
      setAnonymousFeedbackSubmitted(false);
    }
  }, [state.phase]);

  const runAudit = useCallback(async (manualUrl, forceRefresh = false) => {
    const targetUrl = normalizeSiteUrl(typeof manualUrl === "string" ? manualUrl : state.url);
    if (!targetUrl || !isValidWebsiteUrl(targetUrl)) {
      showError("Please enter a valid URL.");
      return;
    }

    const stage = getAppStage();
    const isAlpha = stage === "alpha";
    const isBeta = stage === "beta";
    const isPreview = stage === "preview";

    if (!isAuthenticated) {
      const limit = isAlpha ? 1 : 2;
      const today = new Date().toDateString();
      const countKey = "anon_audits_count";
      const dateKey = "anon_audits_date";

      const storedDate = localStorage.getItem(dateKey);
      let count = 0;
      if (storedDate === today) {
        count = parseInt(localStorage.getItem(countKey) || "0", 10);
      }

      if (count >= limit) {
        if (isAlpha) {
          setPlanGate({
            code: "PLAN_LIMIT_REACHED",
            message: "Visitors are limited to 1 audit per day during Alpha testing.",
            ctaLabel: "Audit Home",
            ctaHref: "/",
          });
          dispatch({ type: "AUDIT_ERROR", payload: "Daily limit reached. Only 1 audit a day is allowed." });
        } else if (isBeta) {
          setPlanGate({
            code: "PLAN_LIMIT_REACHED",
            message: "Visitors are limited to 2 audits per day. Sign up for a free Beta account to unlock 500 audits per month!",
            ctaLabel: "Create Free Account",
            ctaHref: "/login",
          });
          dispatch({ type: "AUDIT_ERROR", payload: "Daily limit reached. 2 audits a day allowed for guests." });
        } else if (isPreview) {
          setPlanGate({
            code: "PLAN_LIMIT_REACHED",
            message: "Visitors are limited to 2 audits per day in Preview mode. Account registration is disabled.",
            ctaLabel: "Audit Home",
            ctaHref: "/",
          });
          dispatch({ type: "AUDIT_ERROR", payload: "Daily limit reached. 2 audits a day allowed for guests." });
        } else {
          setPlanGate({
            code: "PLAN_LIMIT_REACHED",
            message: "Visitors are limited to 2 audits per day. Sign up for a free account to unlock 10 audits per month!",
            ctaLabel: "Create Free Account",
            ctaHref: "/login",
          });
          dispatch({ type: "AUDIT_ERROR", payload: "Daily limit reached. 2 audits a day allowed for guests." });
        }
        return;
      }
    }

    dispatch({ type: "START_AUDIT" });
    reset();
    setPlanGate(null);

    try {
      const { data } = await apiPost("/api/seo/audit/start", {
        url: targetUrl,
        refresh: isAppMode ? forceRefresh : false,
      });
      if (data.auditId) {
        await subscribe(data.auditId, { progressPath: "/api/seo/progress" });
        return;
      }
      throw new Error("Audit did not start");
    } catch (err) {
      const apiData = err?.response?.data || {};
      const status = err?.response?.status;
      if (
        status === 429 ||
        apiData?.code === "PLAN_LIMIT_REACHED" ||
        apiData?.code === "PLAN_FEATURE_REQUIRED"
      ) {
        setPlanGate({
          code: apiData.code || "PLAN_LIMIT_REACHED",
          message: apiData.error || "You have reached your current plan limits.",
          cta: apiData.cta || "Upgrade to Premium to continue.",
        });
        dispatch({ type: "AUDIT_ERROR", payload: apiData.error || "Plan limit reached" });
        return;
      }

      try {
        const { data } = await apiGet("/api/seo/audit", {
          url: targetUrl,
          refresh: isAppMode && forceRefresh ? "true" : "false",
        });
        dispatch({ type: "AUDIT_SUCCESS", payload: data });
        if (process.env.REACT_APP_STAGE === "beta" && !isAuthenticated) {
          localStorage.setItem("last_free_audit_date", new Date().toDateString());
        }
      } catch (fallbackErr) {
        const fallbackApiData = fallbackErr?.response?.data || {};
        if (fallbackApiData?.code === "PLAN_LIMIT_REACHED" || fallbackApiData?.code === "PLAN_FEATURE_REQUIRED") {
          setPlanGate({
            code: fallbackApiData.code,
            message: fallbackApiData.error || "You have reached your current plan limits.",
            cta: fallbackApiData.cta || "Upgrade to Premium to continue.",
          });
          dispatch({ type: "AUDIT_ERROR", payload: fallbackApiData.error || "Plan limit reached" });
          return;
        }
        const msg = parseApiError(fallbackErr, "Error running SEO audit");
        dispatch({ type: "AUDIT_ERROR", payload: msg });
        showError(msg);
      }
    }
  }, [state.url, reset, subscribe, isAuthenticated, showError, isAppMode]);

  const { search } = useLocation();
  const hasAutoStarted = useRef(false);

  useEffect(() => {
    if (hasAutoStarted.current) return;
    const params = new URLSearchParams(search);
    const urlParam = params.get("url");
    if (urlParam) {
      hasAutoStarted.current = true;
      dispatch({ type: "SET_URL", payload: urlParam });
      if (isValidWebsiteUrl(urlParam)) {
        runAudit(urlParam);
      } else {
        showError("The provided URL is invalid. Please check and try again.");
      }
    }
  }, [search, runAudit, showError]);

  const progressOrError = progress || (state.error ? { error: state.error } : null);
  const dismissPlanGate = () => setPlanGate(null);

  const showAnonSaveBanner =
    !isAuthenticated &&
    !isAppMode &&
    state.phase === PHASES.RESULTS &&
    state.report &&
    !state.loading &&
    process.env.REACT_APP_STAGE !== "alpha";

  const hasRealReport = state.phase === PHASES.RESULTS && state.report;
  const shouldRequireAnonymousFeedback =
    !isAuthenticated && !isAppMode && hasRealReport && !anonymousFeedbackSubmitted;

  useEffect(() => {
    if (!shouldRequireAnonymousFeedback || feedbackModalOpen) return undefined;

    const onScroll = () => {
      if (window.scrollY >= 180) {
        setFeedbackModalOpen(true);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [shouldRequireAnonymousFeedback, feedbackModalOpen]);

  useEffect(() => {
    if (!feedbackModalOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [feedbackModalOpen]);

  return (
    <div className="page page-audit">
      <AuditInput
        url={state.url}
        setUrl={(url) => dispatch({ type: "SET_URL", payload: url })}
        onStart={runAudit}
        loading={state.loading}
        mode={isAppMode ? "app" : "guest"}
      />

      {state.phase === PHASES.PROGRESS && (
        <AuditProgress
          progress={progressOrError}
          isAuthenticated={isAuthenticated}
          onRetry={() => {
            reset();
            dispatch({ type: "RESET" });
          }}
        />
      )}

      {(state.phase === PHASES.RESULTS && state.report) || (displayReport && state.phase === PHASES.INPUT) ? (
        <div className="audit-results-shell" style={{ marginTop: 40 }}>
          {displayReport === demoSeoReport && state.phase === PHASES.INPUT && (
            <div className="audit-demo-banner site-panel-empty">
              Example SEO report for <strong>{demoSeoReport.url}</strong>. Enter your URL above to run a live scan.
            </div>
          )}

          {hasRealReport && isAppMode && isAuthenticated && (
            <div
              className="card"
              style={{
                marginBottom: 16,
                padding: "1rem",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <div>
                <div style={{ fontWeight: 600, color: "var(--text-heading)" }}>Need a fresh scan?</div>
                <div style={{ fontSize: "0.9rem", color: "var(--text-muted)" }}>
                  Run the audit again and bypass cached results for the latest snapshot.
                </div>
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => runAudit(state.report.url, true)}
              >
                Force Re-scan
              </button>
            </div>
          )}

          <AuditResults report={displayReport} />

          {displayReport.share_url && (
            <ShareCard
              report={displayReport}
              onShareClick={() => window.open(displayReport.share_url, "_blank", "noopener,noreferrer")}
            />
          )}

          <SeoScoreCard report={displayReport} showHero={false} showSavePromo={true} />

          {hasRealReport && isAppMode && isAuthenticated && (
            <AuditFeedbackWidget auditId={state.report.id} url={state.report.url} />
          )}

          {hasRealReport && isAppMode && isAuthenticated && (
            <>
              <EmbedCard report={state.report} />
              {!isPremium && !upgradeCardDismissed && (
                <div style={{ position: "relative" }}>
                  <button
                    type="button"
                    onClick={() => setUpgradeCardDismissed(true)}
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "1rem",
                      color: "var(--text-muted)",
                      zIndex: 1,
                      lineHeight: 1,
                      padding: "4px 8px",
                    }}
                    aria-label="Dismiss"
                  >
                    ×
                  </button>
                  <PlanUpgradeCard
                    title="Need more audits?"
                    message="Your Free plan has a monthly limit. Upgrade to Solo or Agency for higher limits and deep audit tools."
                    ctaLabel="View Upgrade Options"
                    ctaHref="/pricing"
                  />
                </div>
              )}
            </>
          )}

          {showAnonSaveBanner && (
            <div className="card" style={{ marginBottom: 20, background: "#fef3c7", border: "1px solid #fde68a" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
                <div>
                  <h3 style={{ margin: 0, color: "#92400e", fontSize: "1.1rem" }}>Want to save this audit?</h3>
                  <p style={{ margin: "4px 0 0", color: "#b45309", fontSize: "0.9rem" }}>
                    {process.env.REACT_APP_STAGE === "beta"
                      ? "Create a free Beta account to track historical performance, export PDF reports, and run up to 5 audits per day."
                      : "Create a free account to track historical performance, unlock the Deep Audit, and monitor unlimited sites."}
                  </p>
                </div>
                <a href="/login?mode=signup" className="btn btn-warning">Sign up for free</a>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {planGate && (
        <PlanUpgradeModal
          title={planGate.code === "PLAN_LIMIT_REACHED" ? "Daily limit reached" : "Premium feature required"}
          message={planGate.message || planGate.cta}
          ctaLabel={planGate.ctaLabel || "Upgrade to Premium"}
          ctaHref={planGate.ctaHref || "/settings?tab=workspace"}
          onClose={dismissPlanGate}
        />
      )}

      {feedbackModalOpen && hasRealReport && (
        <FeedbackModal
          auditId={state.report.id}
          url={state.report.url}
          mandatory={shouldRequireAnonymousFeedback}
          onClose={() => {
            setAnonymousFeedbackSubmitted(true);
            setFeedbackModalOpen(false);
          }}
        />
      )}

      {!isAppMode && <WorkflowSteps />}
    </div>
  );
}
